import { FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { WorkflowOrganisation } from "../../../../../../../models";
import { searchProjectOrganisations } from "../../../../../../../service/query";
import { ServiceError, Status } from "../../../../../../../service/Shared";
import { updateWorkflowOrganisation } from "../../../../../../../service/workflow";
import { useAuth } from "../../../../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../../../../utils";
import { getWorkflowTasksBoardRoute } from "../../../../../../../utils/routes";
import { dataGridMapFilterCriteria, Toast } from "../../../../../../../widget";

interface UseWorkflowPermissionsReturnData {
  workflowOrganisations: WorkflowOrganisation[];
  isApplyWorkflowTemplateLoading: boolean;
  showOwnerOrganisationTasksWarning: boolean;
  handleClose: () => void;
  handlePermissionSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleUpdateOrganisationAccessLevel: (uuid: string, accessLevel: string) => void;
  errors?: ServiceError[];
}

interface useWorkflowPermissionsProps {
  workflowUuid: string | undefined;
  projectUuid: string | undefined;
  onClose: () => void;
  show: boolean;
  // Undefined until the board's workflow has loaded — the panel mounts with the board, not on open.
  existingWorkflowOrganisations: WorkflowOrganisation[] | undefined;
}

/*
  Shared so the default for an absent prop keeps one identity across renders. A fresh [] would be a new
  dependency every render, re-running the load effect below and setting state on a loop.
*/
const NO_EXISTING_ORGANISATIONS: WorkflowOrganisation[] = [];

export const useWorkflowPermissions = ({
  workflowUuid,
  projectUuid,
  onClose,
  show,
  existingWorkflowOrganisations = NO_EXISTING_ORGANISATIONS,
}: useWorkflowPermissionsProps): UseWorkflowPermissionsReturnData => {
  const navigate = useNavigate();
  const { currentUserType } = useAuth();
  const [workflowOrganisations, setWorkflowOrganisations] = useState<WorkflowOrganisation[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOwnerOrganisationTasksWarning, setShowOwnerOrganisationTasksWarning] = useState(false);
  const [errors, setErrors] = useState<ServiceError[] | undefined>([]);

  const handleClose = (): void => {
    setShowOwnerOrganisationTasksWarning(false);
    setErrors([]);
    onClose();
  };

  const handleUpdateOrganisationAccessLevel = (uuid: string, accessLevel: string): void => {
    setWorkflowOrganisations(
      workflowOrganisations.map((org) => {
        if (org.uuid === uuid) {
          return { ...org, accessLevel };
        }
        return org;
      })
    );
  };

  const handlePermissionSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (workflowUuid === undefined) return;
    setErrors([]);
    const newUpdatedWorkflowOrganisation = workflowOrganisations
      .filter((org) => org.accessLevel !== undefined && org.accessLevel !== "No access")
      .map((org) => ({
        accessLevel: org.accessLevel as string,
        organisationUuid: org.uuid,
      }));

    const response = await updateWorkflowOrganisation({
      workflowUuid,
      organisations: newUpdatedWorkflowOrganisation,
    });
    if (response.status === Status.Success) {
      Toast.success({ message: "Workflow permission successfully applied" });
      navigate(getWorkflowTasksBoardRoute(workflowUuid, currentUserType));
      setErrors([]);
    }
    if (response.status === Status.Error) {
      setErrors(response.errors);
    }
  }, setIsSubmitting);

  const getAndSetWorkflowOrganisations = useCallback(async (): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);

    filterCriteria.results = {
      role: {
        operator: "in",
        value: "Asset Manager, Developer, Development Manager, Guest - Write, Guest - Read",
      },
    };

    if (!projectUuid) {
      setWorkflowOrganisations(
        [...existingWorkflowOrganisations].sort(
          (a, b) => (b.accessLevel === "Owner" ? 1 : 0) - (a.accessLevel === "Owner" ? 1 : 0)
        )
      );
      return;
    }

    const response = await searchProjectOrganisations({
      projectUuid,
      paging: { beforeCursor: null, afterCursor: null, limit: 10 },
      filter: filterCriteria,
    });

    if (response.status === Status.Success && response.data && response.data?.results.length > 0) {
      const mergedWorkflowOrganisations = response.data.results
        .map(
          (org) =>
            ({
              uuid: org.organisationUuid,
              displayName: org.displayName,
              role: org.role === "Guest - Read" || org.role === "Guest - Write" ? "Guest Developer" : org.role,
              accessLevel: existingWorkflowOrganisations.find((e) => e.uuid === org.organisationUuid)?.accessLevel,
            }) as WorkflowOrganisation
        )
        .sort((a, b) => (b.accessLevel === "Owner" ? 1 : 0) - (a.accessLevel === "Owner" ? 1 : 0));

      setWorkflowOrganisations(mergedWorkflowOrganisations);
    }
  }, [existingWorkflowOrganisations, projectUuid]);

  /*
    The panel is mounted alongside the board rather than on open, so the load waits for `show` — a closed
    panel neither queries the project's organisations nor holds a list that has gone stale since.
  */
  useEffect(() => {
    if (!show) return;
    getAndSetWorkflowOrganisations();
  }, [show, getAndSetWorkflowOrganisations]);

  return {
    showOwnerOrganisationTasksWarning,
    workflowOrganisations,
    handleUpdateOrganisationAccessLevel,
    isApplyWorkflowTemplateLoading: isSubmitting,
    handlePermissionSubmit,
    handleClose,
    errors,
  };
};
